import { reportLog } from 'services/userMonitoring'

/**
 * Represents a traveller detail for insurance validation.
 * firstname and surname are optional to handle cases where
 * the data might not be filled in the form yet.
 */
export interface InsuranceTravellerDetail {
  firstname: string | undefined
  surname: string | undefined
  age_range: string
}

export interface InsuranceTravellerMismatchDetails {
  quoteId: string
  productId: string
  insuranceType: string
  quoteTravellerCount: number
  actualTravellerCount: number
  difference: number
  quoteKey?: string
  itemId: string
  travellersDetails: Array<InsuranceTravellerDetail>
  isUsingCustomTravellers: boolean
  isCruiseCheckout: boolean
}

export interface InsuranceTravellerValidationResult {
  isValid: boolean
  mismatch?: InsuranceTravellerMismatchDetails
}

/**
 * Validates that the number of travellers in the insurance quote matches
 * the number of travellers being submitted in the order.
 *
 * This is a critical validation to prevent XCover rejections due to
 * traveller count mismatches between quote fetching and order submission.
 */
export function validateInsuranceTravellerCount(
  quote: App.InsuranceQuote | undefined,
  travellersDetails: Array<InsuranceTravellerDetail>,
  itemId: string,
  insuranceType: string,
  isUsingCustomTravellers: boolean,
  isCruiseCheckout: boolean,
): InsuranceTravellerValidationResult {
  if (!quote) {
    return { isValid: true } // No quote means no validation needed
  }

  const quoteTravellerCount = quote.travellerCount
  const actualTravellerCount = travellersDetails.length

  if (quoteTravellerCount === actualTravellerCount) {
    return { isValid: true }
  }

  const mismatchDetails: InsuranceTravellerMismatchDetails = {
    quoteId: quote.id,
    productId: quote.productId,
    insuranceType,
    quoteTravellerCount,
    actualTravellerCount,
    difference: actualTravellerCount - quoteTravellerCount,
    quoteKey: quote.key,
    itemId,
    travellersDetails,
    isUsingCustomTravellers,
    isCruiseCheckout,
  }

  return {
    isValid: false,
    mismatch: mismatchDetails,
  }
}

/**
 * Logs insurance traveller count mismatch to monitoring with detailed information
 * for debugging and tracking purposes.
 *
 * This log will help identify:
 * - Which checkout flows are causing mismatches
 * - The specific discrepancy between quote and order
 * - Whether custom travellers feature is involved
 */
export function logInsuranceTravellerMismatch(
  mismatch: InsuranceTravellerMismatchDetails,
  additionalContext?: Record<string, unknown>,
): void {
  const logData = {
    // Core mismatch information
    quoteId: mismatch.quoteId,
    productId: mismatch.productId,
    insuranceType: mismatch.insuranceType,
    quoteTravellerCount: mismatch.quoteTravellerCount,
    actualTravellerCount: mismatch.actualTravellerCount,
    difference: mismatch.difference,
    mismatchDirection: mismatch.difference > 0 ? 'order_has_more' : 'order_has_fewer',

    // Context for debugging
    quoteKey: mismatch.quoteKey,
    itemId: mismatch.itemId,
    isUsingCustomTravellers: mismatch.isUsingCustomTravellers,
    isCruiseCheckout: mismatch.isCruiseCheckout,

    // Traveller details for investigation
    travellersDetailsCount: mismatch.travellersDetails.length,
    travellersAgeRanges: mismatch.travellersDetails.map(t => t.age_range).join(','),

    // Additional context if provided
    ...additionalContext,
  }

  reportLog(
    `Insurance traveller count mismatch: quote expects ${mismatch.quoteTravellerCount} travellers, order has ${mismatch.actualTravellerCount}`,
    logData,
    'warn',
  )

  // Also log to console in development for easier debugging
  if (process.env.NODE_ENV === 'development') {
    console.warn('[Insurance Traveller Mismatch]', {
      message: `Quote expects ${mismatch.quoteTravellerCount} travellers, but order has ${mismatch.actualTravellerCount}`,
      ...logData,
    })
  }
}

/**
 * Validates and logs insurance traveller count mismatches for all insurance items.
 * Returns an array of validation results.
 */
export function validateAndLogInsuranceTravellerCounts(
  validationInputs: Array<{
    quote: App.InsuranceQuote | undefined
    travellersDetails: Array<InsuranceTravellerDetail>
    itemId: string
    insuranceType: string
    isUsingCustomTravellers: boolean
    isCruiseCheckout: boolean
  }>,
  additionalContext?: Record<string, unknown>,
): Array<InsuranceTravellerValidationResult> {
  return validationInputs.map(input => {
    const result = validateInsuranceTravellerCount(
      input.quote,
      input.travellersDetails,
      input.itemId,
      input.insuranceType,
      input.isUsingCustomTravellers,
      input.isCruiseCheckout,
    )

    if (!result.isValid && result.mismatch) {
      logInsuranceTravellerMismatch(result.mismatch, additionalContext)
    }

    return result
  })
}
